import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/pro-regular-svg-icons/faVideo";
import { faVideoSlash } from "@fortawesome/pro-regular-svg-icons/faVideoSlash";
import { faPlay } from "@fortawesome/pro-regular-svg-icons/faPlay";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { useResultFile } from "./useFormResultUpload";

interface Props {
  element: DocumentElementType;
}

/** A static poster image URL for a share link, or null when none is derivable. */
function posterForUrl(url: string): string | null {
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/i,
  );
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  return null;
}

/**
 * Read-only render of a page-document video block. Videos can't play in a
 * static PDF, so this shows the poster frame (YouTube thumbnail or the
 * attachment's media-worker thumbnail) with a play glyph + caption — the
 * faithful static form of the editor's player.
 */
const PageVideoView: React.FC<Props> = ({ element }) => {
  const data = (element.data ?? {}) as {
    attachmentId?: number | null;
    videoUrl?: string;
    caption?: string;
  };
  const attachmentId =
    typeof data.attachmentId === "number" ? data.attachmentId : null;
  const { file, missing } = useResultFile(attachmentId);

  const hasVideo = !!(attachmentId || data.videoUrl);
  if (!hasVideo) return null;

  // An uploaded video that is gone or withheld from this reader, with no link
  // to show in its place: say so instead of posing as a video.
  const unavailable = missing && !data.videoUrl;

  const poster =
    (data.videoUrl ? posterForUrl(data.videoUrl) : null) ??
    file?.thumbnailUrl ??
    null;
  const caption = data.caption?.trim() || null;

  return (
    <figure className="m-0">
      <div className="relative aspect-video overflow-hidden rounded-[11px] bg-[#14120f]">
        {poster ? (
          <img
            src={poster}
            alt={caption ?? ""}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : unavailable ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-[10px] font-jakarta text-[13px] font-medium text-[#8f8b83]">
            <FontAwesomeIcon icon={faVideoSlash} className="text-[24px]" />
            Video unavailable
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#6a6660]">
            <FontAwesomeIcon icon={faVideo} className="text-[28px]" />
          </div>
        )}
        {!unavailable && (
          <span className="absolute left-1/2 top-1/2 flex h-[54px] w-[54px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white">
            <FontAwesomeIcon icon={faPlay} className="ml-[3px] text-[18px]" />
          </span>
        )}
      </div>
      {caption && (
        <figcaption className="mt-[8px] font-jakarta text-[12.5px] font-medium text-[#8f8b83]">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

export default PageVideoView;
